/**
 * Cost Calculation Utility
 *
 * Implements all cost calculation formulas from the Excel spreadsheet
 * for the DEFIT estimating system.
 */
/**
 * Calculate total cost based on item type and Excel formulas
 */
export function calculateTotalCost(input) {
    const { sectionId, title, unitCost, quantity, qty, weeksQty, numberQty, areaInfo, afterHoursEnabled = false, afterHoursMultiplier, shopfrontItemsTotal, floatValue, } = input;
    // Check if this is an "After Hours works Required" item
    const isAfterHoursItem = title.toLowerCase().includes("after hours");
    // Check if this is "Shopfront is OVER 3.6m in height" item (toggle only, no direct cost)
    const isShopfrontOver36m = title.toLowerCase().includes("over 3.6m") ||
        title.toLowerCase().includes("over 3.6 m");
    // If this is the OVER 3.6m toggle item, do not add an extra standalone cost.
    // The height uplift is applied to the underlying shopfront items via shopfrontSettings
    // in the component layer, so this item should contribute $0 itself to avoid double counting.
    if (isShopfrontOver36m && shopfrontItemsTotal !== undefined) {
        return {
            totalCost: 0,
            formula: "Shopfront height toggle applied via item height factors (no direct cost)",
        };
    }
    // Get area values
    const tenancyArea = areaInfo.find((item) => item.label.toLowerCase().includes("tenancy defit"))
        ?.value || 0;
    const wallArea = areaInfo.find((item) => item.label.toLowerCase().includes("wall finish (plaster)"))?.value || 0;
    // Get shopfrontArea for shopfronts and hoardings sections (ONLY "Shopfront (under 3.6m) OR of Kiosk Lm (m2)", NOT l/m)
    const shopfrontAreaForShopfronts = areaInfo.find((item) => item.label.toLowerCase().includes("shopfront (under 3.6m)") ||
        item.label.toLowerCase().includes("kiosk lm (m2)"))?.value || 0;
    // After hours factor - use provided multiplier or default to 1.3 if enabled
    const afterHoursFactor = afterHoursEnabled
        ? afterHoursMultiplier ?? 1.3
        : 1.0;
    let totalCost = 0;
    let formula = "";
    // HOARDINGS
    if (sectionId === "hoardings") {
        // Unit Cost * Shopfront (under 3.6m) OR of Kiosk Lm (m2)
        totalCost = unitCost * shopfrontAreaForShopfronts;
        formula = `$${unitCost} × ${shopfrontAreaForShopfronts}m² = $${totalCost.toFixed(2)}`;
    }
    // COMPLIANCE
    else if (sectionId === "permits") {
        // Unit Cost * 1
        totalCost = unitCost * 1;
        formula = `$${unitCost} × 1 = $${totalCost.toFixed(2)}`;
    }
    // SERVICE TRADES
    else if (sectionId === "electrical") {
        // Unit Cost * 1
        totalCost = unitCost * 1;
        formula = `$${unitCost} × 1 = $${totalCost.toFixed(2)}`;
    }
    else if (sectionId === "fire") {
        // Unit Cost * Qty
        // For items without quantity, use 1 instead of 0
        const qtyValue = quantity || qty || 1;
        totalCost = unitCost * qtyValue;
        formula = `$${unitCost} × ${qtyValue} = $${totalCost.toFixed(2)}`;
    }
    else if (sectionId === "hydraulics") {
        // Unit Cost * Qty (for additional points) or Unit Cost * 1 (for attendance)
        const qtyValue = quantity || qty || 0;
        if (title.toLowerCase().includes("additional")) {
            totalCost = unitCost * qtyValue;
            formula = `$${unitCost} × ${qtyValue} = $${totalCost.toFixed(2)}`;
        }
        else {
            totalCost = unitCost * 1;
            formula = `$${unitCost} × 1 = $${totalCost.toFixed(2)}`;
        }
    }
    else if (sectionId === "hvac") {
        // Unit Cost * 1
        totalCost = unitCost * 1;
        formula = `$${unitCost} × 1 = $${totalCost.toFixed(2)}`;
    }
    // FLOORING
    else if (sectionId === "floor-finishes") {
        // Unit Cost * Tenancy Defit (m2) * After Hours Factor
        // After Hours item itself doesn't use the factor
        if (isAfterHoursItem) {
            totalCost = unitCost * tenancyArea;
            formula = `$${unitCost} × ${tenancyArea}m² = $${totalCost.toFixed(2)}`;
        }
        else {
            totalCost = unitCost * tenancyArea * afterHoursFactor;
            formula = `$${unitCost} × ${tenancyArea}m² × ${afterHoursFactor} = $${totalCost.toFixed(2)}`;
        }
    }
    else if (sectionId === "screeds") {
        // Unit Cost * Tenancy Defit (m2) * After Hours Factor
        // After Hours item itself doesn't use the factor
        if (isAfterHoursItem) {
            totalCost = unitCost * tenancyArea;
            formula = `$${unitCost} × ${tenancyArea}m² = $${totalCost.toFixed(2)}`;
        }
        else {
            totalCost = unitCost * tenancyArea * afterHoursFactor;
            formula = `$${unitCost} × ${tenancyArea}m² × ${afterHoursFactor} = $${totalCost.toFixed(2)}`;
        }
    }
    else if (sectionId === "grinding") {
        // Unit Cost * Tenancy Defit (m2) * After Hours Factor
        // After Hours item itself doesn't use the factor
        if (isAfterHoursItem) {
            totalCost = unitCost * tenancyArea;
            formula = `$${unitCost} × ${tenancyArea}m² = $${totalCost.toFixed(2)}`;
        }
        else {
            totalCost = unitCost * tenancyArea * afterHoursFactor;
            formula = `$${unitCost} × ${tenancyArea}m² × ${afterHoursFactor} = $${totalCost.toFixed(2)}`;
        }
    }
    // INTERNAL DEMOLITION
    else if (sectionId === "kiosks") {
        // Unit Cost * Tenancy Defit (m2)
        totalCost = unitCost * tenancyArea;
        formula = `$${unitCost} × ${tenancyArea}m² = $${totalCost.toFixed(2)}`;
    }
    else if (sectionId === "joinery") {
        // Unit Cost * Tenancy Defit (m2)
        totalCost = unitCost * tenancyArea;
        formula = `$${unitCost} × ${tenancyArea}m² = $${totalCost.toFixed(2)}`;
    }
    else if (sectionId === "walls") {
        // Unit Cost * Wall Finish (Plaster) (m2) * After Hours Factor
        // After Hours item itself doesn't use the factor
        if (isAfterHoursItem) {
            totalCost = unitCost * wallArea;
            formula = `$${unitCost} × ${wallArea}m² = $${totalCost.toFixed(2)}`;
        }
        else {
            totalCost = unitCost * wallArea * afterHoursFactor;
            formula = `$${unitCost} × ${wallArea}m² × ${afterHoursFactor} = $${totalCost.toFixed(2)}`;
        }
    }
    else if (sectionId === "shopfronts") {
        // Base calculation: Unit Cost × "Shopfront (under 3.6m) OR of Kiosk Lm (m2)" value (NOT l/m)
        // Note: Height factor is NOT applied here - it will be applied in the component layer
        // after calculation if the "Shopfront is OVER 3.6m in height" toggle is ON
        totalCost = unitCost * shopfrontAreaForShopfronts;
        // Apply after hours factor (After Hours item itself doesn't use the factor)
        if (!isAfterHoursItem) {
            totalCost *= afterHoursFactor;
            formula = `$${unitCost} × ${shopfrontAreaForShopfronts}m² × ${afterHoursFactor} (after hours) = $${totalCost.toFixed(2)}`;
        }
        else {
            formula = `$${unitCost} × ${shopfrontAreaForShopfronts}m² = $${totalCost.toFixed(2)}`;
        }
    }
    else if (sectionId === "ceiling") {
        // Unit Cost * Tenancy Defit (m2) * After Hours Factor
        // After Hours item itself doesn't use the factor
        if (isAfterHoursItem) {
            totalCost = unitCost * tenancyArea;
            formula = `$${unitCost} × ${tenancyArea}m² = $${totalCost.toFixed(2)}`;
        }
        else {
            totalCost = unitCost * tenancyArea * afterHoursFactor;
            formula = `$${unitCost} × ${tenancyArea}m² × ${afterHoursFactor} = $${totalCost.toFixed(2)}`;
        }
    }
    // PRELIMINARY COST
    else if (sectionId === "prelims") {
        // Unit Cost * 1
        totalCost = unitCost * 1;
        formula = `$${unitCost} × 1 = $${totalCost.toFixed(2)}`;
    }
    else if (sectionId === "scissor-lift") {
        // For scissor lift items: Unit Cost * No. Weeks
        // For "19" Electric: Unit Cost * No. Weeks + Float Value
        const weeksValue = weeksQty || 0;
        const is19Electric = title.toLowerCase().includes("19") &&
            title.toLowerCase().includes("electric");
        if (is19Electric && floatValue !== undefined) {
            totalCost = unitCost * weeksValue + floatValue;
            formula = `$${unitCost} × ${weeksValue} weeks + $${floatValue} (Float) = $${totalCost.toFixed(2)}`;
        }
        else {
            totalCost = unitCost * weeksValue;
            formula = `$${unitCost} × ${weeksValue} weeks = $${totalCost.toFixed(2)}`;
        }
    }
    else if (sectionId === "skip-hire") {
        // Unit Cost * No.
        const numberValue = numberQty || 0;
        totalCost = unitCost * numberValue;
        formula = `$${unitCost} × ${numberValue} = $${totalCost.toFixed(2)}`;
    }
    else if (sectionId === "other-fees") {
        // Unit Cost * 1
        totalCost = unitCost * 1;
        formula = `$${unitCost} × 1 = $${totalCost.toFixed(2)}`;
    }
    // Default fallback
    else {
        totalCost = unitCost * 1;
        formula = `$${unitCost} × 1 = $${totalCost.toFixed(2)}`;
    }
    return {
        totalCost: Math.round(totalCost * 100) / 100, // Round to 2 decimal places
        formula,
    };
}
/**
 * Get the after hours status for a section
 */
export function getSectionAfterHoursStatus(accordionData, category, sectionId) {
    // Find the section and check if "After Hours works Required" item is enabled
    const categoryData = accordionData[category];
    if (!categoryData)
        return false;
    for (const accordionItem of categoryData) {
        if (accordionItem.sections) {
            const section = accordionItem.sections.find((s) => s.sectionId === sectionId);
            if (section) {
                const afterHoursItem = section.items.find((item) => item.title.toLowerCase().includes("after hours"));
                return afterHoursItem?.inScope || false;
            }
        }
    }
    return false;
}
/**
 * Helper function to find height factor from cost parameters
 */
function getHeightFactorFromCostParams(costParameters, id) {
    if (!costParameters || costParameters.length === 0)
        return null;
    const item = costParameters.find((item) => item.itemId?.toLowerCase().includes(id));
    return item?.unitCost ?? null;
}
/**
 * Get shopfront height settings for a section
 */
export function getShopfrontSettings(accordionData, category, sectionId, costParameters) {
    const categoryData = accordionData[category];
    if (!categoryData) {
        return { heightEnabled: false, stdHeightFactor: 1, hvyHeightFactor: 1 };
    }
    // Find the height toggle item (id: shopfront-3a or title contains "over 3.6m")
    let heightEnabled = false;
    for (const accordionItem of categoryData) {
        if (accordionItem.sections) {
            const section = accordionItem.sections.find((s) => s.sectionId === sectionId);
            if (section) {
                const heightToggleItem = section.items.find((item) => item.id === "shopfront-3a" ||
                    item.title.toLowerCase().includes("over 3.6m"));
                heightEnabled = heightToggleItem?.inScope === true;
                break;
            }
        }
    }
    // Get height factors from cost parameters if available, otherwise use defaults
    const stdFactor = costParameters
        ? getHeightFactorFromCostParams(costParameters, "shopfront-3") ?? 1
        : 1;
    const hvyFactor = costParameters
        ? getHeightFactorFromCostParams(costParameters, "shopfront-4") ?? 1
        : 1;
    return {
        heightEnabled,
        stdHeightFactor: stdFactor,
        hvyHeightFactor: hvyFactor,
        heavyDuty: false, // Deprecated - kept for backward compatibility
    };
}
/**
 * Get shopfront height settings from an items array (for use when items are already updated)
 * This reads directly from the items array instead of accordionData to avoid stale state issues
 * When toggle is YES, uses stored input values from shopfront-3/4 items, otherwise uses costsettings defaults
 */
export function getShopfrontSettingsFromItems(items, costParameters) {
    // Find the height toggle item
    const heightToggleItem = items.find((item) => item.id === "shopfront-3a");
    const heightEnabled = heightToggleItem?.inScope === true;
    // Get height factors - use stored input values if toggle is YES, otherwise use costsettings
    let stdFactor = 1;
    let hvyFactor = 1;
    if (heightEnabled) {
        // Toggle is YES - use stored input values from shopfront-3/4 items
        const shopfront3Item = items.find((item) => item.id === "shopfront-3");
        const shopfront4Item = items.find((item) => item.id === "shopfront-4");
        if (shopfront3Item?.unitCost) {
            const parsed = parseFloat(shopfront3Item.unitCost.toString().replace(/[^0-9.-]/g, ""));
            if (!isNaN(parsed)) {
                stdFactor = parsed;
            }
        }
        else if (costParameters) {
            // Fallback to costsettings if no stored value
            stdFactor = getHeightFactorFromCostParams(costParameters, "shopfront-3") ?? 1;
        }
        if (shopfront4Item?.unitCost) {
            const parsed = parseFloat(shopfront4Item.unitCost.toString().replace(/[^0-9.-]/g, ""));
            if (!isNaN(parsed)) {
                hvyFactor = parsed;
            }
        }
        else if (costParameters) {
            // Fallback to costsettings if no stored value
            hvyFactor = getHeightFactorFromCostParams(costParameters, "shopfront-4") ?? 1;
        }
    }
    else {
        // Toggle is NO - use costsettings defaults
        if (costParameters) {
            stdFactor = getHeightFactorFromCostParams(costParameters, "shopfront-3") ?? 1;
            hvyFactor = getHeightFactorFromCostParams(costParameters, "shopfront-4") ?? 1;
        }
    }
    return {
        heightEnabled,
        stdHeightFactor: stdFactor,
        hvyHeightFactor: hvyFactor,
    };
}
